import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Loader2, BellRing, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WaitlistManager({ eventId, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [num, setNum] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [settings, setSettings] = useState({ closed: false, message: '' });
  const [msgDraft, setMsgDraft] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/events/${eventId}/waitlist`, { headers })).data); }
    catch { /* none yet */ }
    try {
      const i = (await axios.get(`${API}/events/${eventId}/waitlist/info`)).data;
      setSettings({ closed: !!i.closed, message: i.message || '' });
      setMsgDraft(i.message || '');
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/waitlist/settings`, patch, { headers });
      setSettings(s => ({ closed: 'waitlist_closed' in r.data ? r.data.waitlist_closed : s.closed,
        message: 'waitlist_message' in r.data ? r.data.waitlist_message : s.message }));
      toast.success('Waitlist settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSavingSettings(false); }
  };

  const release = async () => {
    const n = parseInt(num || 0, 10);
    if (!n) return toast.error('How many tickets to release?');
    if (!window.confirm(`Release ${n} ticket(s)? The next people in line with a saved card are charged immediately.`)) return;
    setReleasing(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/waitlist/release`, { num_tickets: n }, { headers });
      toast.success(`Released ${r.data.released} ticket(s) → ${r.data.orders} order(s) charged${r.data.payment_failed?.length ? `, ${r.data.payment_failed.length} card(s) failed` : ''}`);
      setNum(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Release failed'); }
    finally { setReleasing(false); }
  };

  const stats = data?.stats || {};
  const entries = data?.entries || [];
  const closed = !!settings.closed;
  const badge = (s) => ({
    waiting: 'bg-amber-500/20 text-amber-400', charged: 'bg-emerald-500/20 text-emerald-400',
    payment_failed: 'bg-rose-500/20 text-rose-400', cancelled: 'bg-slate-500/20 text-slate-400',
    closed: 'bg-slate-500/20 text-slate-400',
  }[s] || 'bg-slate-500/20 text-slate-400');

  return (
    <div className="space-y-3 mt-6" data-testid="waitlist-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2"><BellRing className="h-4 w-4 text-amber-400" /> Waitlist</h3>
          <p className="text-xs text-muted-foreground">Shows automatically on the event page & widget when tickets sell out</p>
        </div>
        <div className="flex gap-3 text-center">
          {[['Waiting', stats.waiting], ['Card saved', stats.with_card], ['Tickets wanted', stats.tickets_wanted], ['Charged', stats.charged]].map(([l, v]) => (
            <div key={l}><p className="text-sm font-bold">{v || 0}</p><p className="text-[9px] uppercase text-muted-foreground">{l}</p></div>
          ))}
        </div>
      </div>
      {(stats.waiting || 0) > 0 && (
        <Card className="border-emerald-500/30"><CardContent className="py-3 flex items-center gap-2">
          <Zap className="h-4 w-4 text-emerald-400" />
          <Input type="number" min={1} className="w-28" placeholder="# tickets" value={num} onChange={e => setNum(e.target.value)} data-testid="waitlist-release-num" />
          <Button size="sm" onClick={release} disabled={releasing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="waitlist-release-btn">
            {releasing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Release & auto-charge next in line'}
          </Button>
          <p className="text-[10px] text-muted-foreground ml-auto max-w-[200px]">In order; anyone wanting more tickets than remain is skipped (keeps their place)</p>
        </CardContent></Card>
      )}
      <Card className="border-border/60"><CardContent className="py-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold">{closed ? '🔒 Waitlist is closed' : '🟢 Waitlist is open'}</p>
            <p className="text-[10px] text-muted-foreground">{closed ? 'No new sign-ups accepted. Auto-closes for good when the event ends.' : 'Guests can join when tickets sell out. Auto-closes after the event ends.'}</p>
          </div>
          <Button size="sm" variant="outline" disabled={savingSettings}
            className={closed ? 'text-emerald-400 border-emerald-500/40' : 'text-rose-400 border-rose-500/40'}
            onClick={() => saveSettings({ closed: !closed })} data-testid="waitlist-toggle-btn">
            {savingSettings ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : closed ? 'Reopen waitlist' : 'Close waitlist'}
          </Button>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Message guests see on the waitlist (optional)" value={msgDraft}
            onChange={e => setMsgDraft(e.target.value)} className="text-xs" data-testid="waitlist-message-input" />
          <Button size="sm" variant="outline" disabled={savingSettings || msgDraft === settings.message}
            onClick={() => saveSettings({ message: msgDraft })} data-testid="waitlist-message-save">Save</Button>
        </div>
      </CardContent></Card>
      <div className="space-y-1">
        {entries.length === 0 && <p className="text-xs text-muted-foreground italic">Nobody on the waitlist yet.</p>}
        {entries.map(e => (
          <div key={e.wl_id} className="flex items-center gap-2 text-xs border-b border-border/40 py-1.5" data-testid={`wl-entry-${e.wl_id}`}>
            {e.position && <span className="font-mono text-muted-foreground w-8">#{e.position}</span>}
            <span className="font-medium">{e.name}</span>
            <span className="text-muted-foreground truncate max-w-[160px]">{e.email}</span>
            <span className="text-muted-foreground">×{e.qty}</span>
            {e.card_saved && e.status === 'waiting' && <span className="text-[9px] text-emerald-400 font-semibold">💳 card saved</span>}
            {e.charged_cents > 0 && <span className="text-emerald-400 font-mono">${(e.charged_cents / 100).toFixed(2)}</span>}
            <Badge className={`ml-auto text-[9px] border-0 ${badge(e.status)}`}>{e.status.replace('_', ' ')}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}
